// components/received/DeclineConfirmationModal.jsx
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Form, Button, Alert } from 'react-bootstrap';

const DeclineConfirmationModal = ({ show, onHide, onConfirm, isLoading, inheritanceName }) => {
  const [confirmationText, setConfirmationText] = useState('');
  const [validationError, setValidationError] = useState('');
  
  const expectedText = 'I understand that I will lose access to this Endowment';
  
  // Reset form when modal is shown/hidden
  useEffect(() => {
    if (show) {
      setConfirmationText('');
      setValidationError('');
    }
  }, [show]);
  
  // Check if confirmation text is valid
  const isValidConfirmation = confirmationText === expectedText;
  
  // Handle input change
  const handleInputChange = (e) => {
    const value = e.target.value;
    setConfirmationText(value);
    
    // Clear validation error when user starts typing
    if (validationError) {
      setValidationError('');
    }
  };
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!isValidConfirmation) {
      setValidationError('Please type the exact confirmation text to proceed.');
      return;
    }
    
    onConfirm(confirmationText);
  };
  
  // Handle cancel
  const handleCancel = () => {
    setConfirmationText('');
    setValidationError('');
    onHide();
  };
  
  return (
    <Modal
      show={show}
      onHide={handleCancel}
      centered
      backdrop="static"
      keyboard={false}
      className="re-decline-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title className="text-danger">
          <i className="fas fa-exclamation-triangle me-2"></i>
          Warning: Decline Endowment
        </Modal.Title>
      </Modal.Header>
      
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Alert variant="warning" className="mb-3">
            <strong>Important:</strong> By declining this Endowment, you will permanently lose access to all its contents.
          </Alert>
          
          {inheritanceName && (
            <p className="mb-3 text-white">
              <strong>Endowment:</strong> {inheritanceName}
            </p>
          )}
          
          <p className="mb-2 text-white">To confirm, please type:</p>
          <p className="font-italic text-muted mb-3">
            "{expectedText}"
          </p>
          
          <Form.Group className="mb-3">
            <Form.Control
              type="text"
              value={confirmationText}
              onChange={handleInputChange}
              placeholder="Type the confirmation text here"
              isInvalid={!!validationError || (confirmationText !== '' && !isValidConfirmation)}
              disabled={isLoading}
              autoFocus
            />
            <Form.Control.Feedback type="invalid">
              {validationError || 'Please type the exact confirmation text to proceed.'}
            </Form.Control.Feedback>
          </Form.Group>
          
          {/* Show character-by-character validation */}
          {confirmationText && (
            <div className="confirmation-progress mb-3">
              <small className="text-muted">
                Progress: {confirmationText.length}/{expectedText.length} characters
                {isValidConfirmation && (
                  <span className="text-success ms-2">
                    <i className="fas fa-check-circle"></i> Correct!
                  </span>
                )}
              </small>
            </div>
          )}
        </Modal.Body>
        
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="danger"
            disabled={!isValidConfirmation || isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Processing...
              </>
            ) : (
              <>
                <i className="fas fa-times-circle me-2"></i>
                Decline Endowment
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

DeclineConfirmationModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  inheritanceName: PropTypes.string
};

DeclineConfirmationModal.defaultProps = {
  isLoading: false,
  inheritanceName: ''
};

export default DeclineConfirmationModal;